// live-shared.tsx — primitives shared by the live standup board (/live) and the per-person live
// page (/live/:userId). Internal to the live-standup feature; not a general-purpose module.
// avColor/initials/activity are the per-surface-duplicated helpers (TeamMembersSurface carries its
// own copies) — this copy is seeded by user_id like TeamMembersSurface's.
//
// Redesign port 2026-07: markup is Tailwind under the `.rd` root (cream tokens, DM Mono meta);
// the LOGIC (activity thresholds, W4 future guard, dot vocabulary 'grn'/'amb', source titles)
// is unchanged and unit-covered by live-shared.test.ts.

import type { ReactNode } from 'react';
import type { TeamLiveMember, TeamLiveTrace, TeamLiveSource } from '../../hooks/useTeamLiveSurface';
import { relativeTime } from '../../lib/formatting';
import { SourceIcon } from '../shared/SourceIcon';
import { siteLabel } from '../../lib/sites';

// Deterministic avatar tone from a stable seed (user_id), so a member keeps the same color.
const AV_COLORS = ['blue', 'sage', 'sand', 'lilac', 'clay', 'teal'] as const;
export function avColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (Math.imul(h, 31) + seed.charCodeAt(i)) >>> 0;
  return AV_COLORS[h % AV_COLORS.length];
}

// Tone → the redesign's pastel avatar pair (prototype live-page palette). Same in dark — these
// are identity colors, not surface colors.
const AV_TONES: Record<string, { bg: string; ink: string }> = {
  blue: { bg: '#dce8ec', ink: '#527c90' },
  sage: { bg: '#e4eadf', ink: '#62725c' },
  sand: { bg: '#eee5d5', ink: '#887157' },
  lilac: { bg: '#e8e2f0', ink: '#4a3d6b' },
  clay: { bg: '#f5d8ce', ink: '#b95c4b' },
  teal: { bg: '#f1e3ce', ink: '#8b6546' },
};

/** Square pastel avatar — seed-stable color; the signed-in user renders on the accent. */
export function AvatarSq({ seed, name, you, size = 40 }: { seed: string; name: string; you?: boolean; size?: 32 | 40 }) {
  const tone = AV_TONES[avColor(seed)] ?? AV_TONES.sand;
  const cls = size === 40 ? 'size-10 rounded-xl text-[15px]' : 'size-8 rounded-lg text-[13px]';
  if (you) {
    return <span className={`grid shrink-0 place-items-center font-semibold ${cls} bg-acc-soft text-acc`}>{initials(name)}</span>;
  }
  return (
    <span className={`grid shrink-0 place-items-center font-semibold ${cls}`} style={{ backgroundColor: tone.bg, color: tone.ink }}>
      {initials(name)}
    </span>
  );
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Activity dot + label from last_active (null-safe). Green within a day, amber past it.
export function activity(lastActive?: string | null): { dot: string | null; label: string } {
  if (!lastActive) return { dot: null, label: 'no recent activity' };
  const mins = (Date.now() - new Date(lastActive).getTime()) / 60000;
  // W4 (defensive): a future last_active is NOT "active now" — there's no such thing as future
  // activity. Treat it as no recent activity (no green dot) rather than faking a live presence.
  if (mins < 0) return { dot: null, label: 'no recent activity' };
  if (mins < 5) return { dot: 'grn', label: 'active now' };
  if (mins < 60 * 24) return { dot: 'grn', label: relativeTime(lastActive) };
  return { dot: 'amb', label: relativeTime(lastActive) };
}

// The activity() dot vocabulary rendered as a redesign dot (grn=green, amb=amber).
const DOT_BG: Record<string, string> = { grn: 'bg-[#62725c]', amb: 'bg-[#c99b52]', rose: 'bg-[#b95c4b]' };
export function ActDot({ tone }: { tone: string | null }) {
  if (!tone) return null;
  return <span className={`size-1.5 shrink-0 rounded-full ${DOT_BG[tone] ?? 'bg-[#62725c]'}`} />;
}

export function withinDay(iso?: string | null): boolean {
  return !!iso && Date.now() - new Date(iso).getTime() < 24 * 60 * 60 * 1000;
}

// Most-recently-active first; never-active members sink to the end.
export function byActivity(a: TeamLiveMember, b: TeamLiveMember): number {
  const ta = a.last_active ? new Date(a.last_active).getTime() : 0;
  const tb = b.last_active ? new Date(b.last_active).getTime() : 0;
  return tb - ta;
}

// InMeeting — a live presence chip shown ONLY while the teammate is currently in a meeting. This is
// a live STATE (sourced from calendar_events server-side), not an activity feed row — it never goes
// through recent[]/relativeTime, so it can't fake "just now". The name renders ONLY when present:
// the backend sends `name` only to invited viewers and omits it (null) otherwise, so a null name
// shows the busy-state alone ("In a meeting") with the title never reaching a non-invitee's client.
export function InMeeting({ member }: { member: TeamLiveMember }) {
  const im = member.in_meeting;
  if (!im) return null;
  const name = im.name?.trim() || null;
  return (
    <span
      className="flex items-center gap-1.5 text-[12px] text-muted-foreground"
      title={name ? `In a meeting: ${name}` : 'In a meeting'}
    >
      <ActDot tone="grn" />In a meeting{name ? `: ${name}` : ''}
    </span>
  );
}

// LiveLine — the board card's "what they're doing now", as a boxless, actively-typing terminal
// line: a green prompt, the focus as the command, a blinking caret. No panel, no label, no accent
// chrome. Idle members get a plain quiet line instead.
export function LiveLine({ member }: { member: TeamLiveMember }) {
  const act = activity(member.last_active);
  if (!member.focus) {
    return (
      <p className="mt-4 font-['DM_Mono'] text-[12px] italic text-muted-foreground">
        {act.dot ? 'heads-down, nothing shared yet' : 'nothing on the board today'}
      </p>
    );
  }
  return (
    <p className="mt-4 flex items-baseline gap-2 font-['DM_Mono'] text-[13px] leading-5">
      <span className="shrink-0 text-[#62725c]">&#10095;</span>
      <span>
        {member.focus.headline}
        <span className="ml-1 inline-block h-[13px] w-[7px] translate-y-0.5 animate-pulse bg-[#62725c]" />
      </span>
    </p>
  );
}

// LiveStatement — the expanded page's version: no terminal flair, just the focus as a clean statement.
export function LiveStatement({ member }: { member: TeamLiveMember }) {
  const act = activity(member.last_active);
  if (!member.focus) {
    return (
      <p className="font-['DM_Mono'] text-[12px] italic text-muted-foreground">
        {act.dot ? 'heads-down, nothing shared yet' : 'nothing on the board today'}
      </p>
    );
  }
  return (
    <div>
      <p className="text-[17px] font-medium leading-6">{member.focus.headline}</p>
      {member.focus.detail && <p className="mt-1 text-sm leading-5 text-muted-foreground">{member.focus.detail}</p>}
    </div>
  );
}

// Tooltip / accessible label for a source chip. Workspace Slack (D2) says so explicitly; a quiet
// connected source reads "connected · quiet today"; an activity-only source (no integration row —
// e.g. the Claude Code CLI) just describes its activity.
export function sourceTitle(s: TeamLiveSource): string {
  if (s.workspace) return 'Slack · connected for the whole workspace';
  const label = siteLabel(s.site);
  if (s.events_24h > 0) return `${label} · ${s.events_24h} in the last day`;
  return s.connected ? `${label} · connected · quiet today` : `${label} · no recent activity`;
}

// SourcesStrip — the board card's compact one-line source row: an icon per connected/active source
// with its 24h event count; quiet (0-count) sources dim to a connected-but-idle state.
export function SourcesStrip({ sources }: { sources: TeamLiveSource[] }) {
  if (!sources.length) return null;
  return (
    <div className="mt-2.5 flex flex-wrap gap-1.5">
      {sources.map((s) => (
        <span
          key={s.site}
          className={`flex items-center gap-1 rounded-full border border-border px-1.5 py-0.5 ${s.events_24h === 0 ? 'opacity-45' : ''}`}
          title={sourceTitle(s)}
        >
          <SourceIcon id={s.site} size={13} />
          {s.events_24h > 0 && <span className="font-['DM_Mono'] text-[9px] text-muted-foreground">{s.events_24h}</span>}
        </span>
      ))}
    </div>
  );
}

export function BlockedCallout({ blockers }: { blockers: string[] }) {
  if (!blockers.length) return null;
  return (
    <div className="mt-3 space-y-1.5">
      {blockers.map((b, i) => (
        <p key={i} className="rounded-xl border border-[#e2aa9c] bg-[#fdf0e7] px-3 py-2 text-[12px] leading-4 text-[#8c5040] dark:border-destructive/40 dark:bg-destructive/10 dark:text-destructive">
          <span className="font-['DM_Mono'] text-[9px] uppercase tracking-[.14em] text-[#bd5b4c] dark:text-destructive">Blocked · </span>
          {b}
        </p>
      ))}
    </div>
  );
}

// Trace — repo · branch · PR# · diffstat · file, in mono (functional code metadata, not a
// decorative accent). Renders nothing when there's nothing to point at. `withFile` includes the
// changed path (record rows); workstreams omit it (they're not a single file).
export function Trace({ trace, withFile }: { trace?: TeamLiveTrace | null; withFile?: boolean }) {
  if (!trace) return null;
  const hasDiff = typeof trace.additions === 'number' || typeof trace.deletions === 'number';
  const parts: ReactNode[] = [];
  if (trace.repo) parts.push(<span key="repo">{trace.repo}</span>);
  if (trace.branch) parts.push(<span key="branch">{trace.branch}</span>);
  if (typeof trace.pr === 'number') parts.push(<span key="pr">PR #{trace.pr}</span>);
  if (withFile && trace.path) parts.push(<span key="path" className="break-all">{trace.path}</span>);
  if (!parts.length && !hasDiff) return null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-2 font-['DM_Mono'] text-[10px] text-muted-foreground">
      {parts.map((p, i) => (
        <span key={i} className="flex items-center gap-2">
          {i > 0 && <span className="opacity-60">·</span>}
          {p}
        </span>
      ))}
      {hasDiff && (
        <span className="flex items-center gap-1.5">
          {typeof trace.additions === 'number' && <span className="text-[#4f7d58]">+{trace.additions}</span>}
          {typeof trace.deletions === 'number' && <span className="text-[#b95c4b]">&minus;{trace.deletions}</span>}
        </span>
      )}
    </div>
  );
}
